import React, {useEffect, useRef, useState} from 'react';
import {useToast} from "@chakra-ui/react";
import {Drawer, Form, Select as AntSelect} from "antd";
import {Input} from "@nextui-org/react";
import {doc, getFirestore, updateDoc, collection, query, where, getDocs, Timestamp} from "firebase/firestore";
import {getDownloadURL, getStorage, ref, uploadBytes} from "firebase/storage";
import Firebase from "../../Firebase/Firebase";
import {Tag} from "primereact/tag";
import {Button} from "@nextui-org/react";
import moment from "moment";
import holder from '../../images/holder.jpg';


const db       = getFirestore(Firebase);
const storage  = getStorage(Firebase);

type Props = {
    seller: any;
    openDrawer: boolean;
    closeDrawer: (val: boolean) => void;
};


const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                 padding:'8px 0', borderBottom:'1px solid #f5f5f5', fontSize:13}}>
        <span style={{color:'#888'}}>{label}</span>
        <span style={{fontWeight:500}}>{value ?? '—'}</span>
    </div>
);

const EditSeller: React.FC<Props> = ({openDrawer, closeDrawer, seller}) => {
    const toast     = useToast();
    const [form]    = Form.useForm();
    const [saving, setSaving]       = useState(false);
    const [imageFile, setImageFile] = useState<string | null>(null);
    const [file, setFile]           = useState<File | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    // Payment receiving methods for this seller
    const [paymentMethods, setPaymentMethods] = useState<any[]>([]);

    const uid = seller?.Uid ?? seller?.uid ?? seller?.sellerID;

    // Pre-fill form whenever seller changes
    useEffect(() => {
        if (!seller) return;
        setImageFile(null);
        setFile(null);
        form.setFieldsValue({
            fullName:   seller.fullName  ?? seller.Fullname ?? '',
            location:   seller.location  ?? seller.Location ?? '',
            userStatus: (seller.userStatus ?? 'active').toLowerCase(),
            trustTier:  seller.trustTier ?? 'none',
            kycExpiresAt: seller.kycExpiresAt?.toDate
                ? moment(seller.kycExpiresAt.toDate()).format('YYYY-MM-DD')
                : '',
        });
    }, [seller, openDrawer]);

    // Fetch payment methods whenever drawer opens for this seller
    useEffect(() => {
        if (!openDrawer || !uid) return;
        getDocs(query(collection(db, 'paymentReceivingMethods'), where('userId', '==', uid)))
            .then(snap => {
                const methods: any[] = [];
                snap.forEach(d => methods.push({id: d.id, ...d.data()}));
                setPaymentMethods(methods);
            })
            .catch(() => setPaymentMethods([]));
    }, [openDrawer, uid]);

    const handleSave = async (values: any) => {
        if (!uid) {
            toast({position:'top', title:'Cannot identify seller UID', status:'error', duration:3000, isClosable:true});
            return;
        }
        setSaving(true);
        try {
            const update: any = {
                fullName:   values.fullName,
                location:   values.location,
                userStatus: values.userStatus,
                trustTier:  values.trustTier,
            };

            // If marking as trusted, set/extend KYC expiry to 1 year from now
            if (values.trustTier === 'trusted') {
                update.kycExpiresAt = Timestamp.fromDate(
                    values.kycExpiresAt
                        ? new Date(values.kycExpiresAt)
                        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
                );
            } else if (values.trustTier !== 'trusted') {
                // Clear KYC expiry if not trusted
                update.kycExpiresAt = null;
            }

            // Upload profile image if changed
            if (file) {
                const snap = await uploadBytes(ref(storage, `userImages/${uid}`), file);
                const url  = await getDownloadURL(snap.ref);
                update.userImage = url;
            }

            await updateDoc(doc(db, 'users', uid), update);

            toast({position:'top', title:'Seller updated', status:'success', duration:2500, isClosable:true});
            closeDrawer(false);
        } catch (e: any) {
            toast({position:'top', title: e.message ?? 'Failed to save', status:'error', duration:4000, isClosable:true});
        } finally {
            setSaving(false);
        }
    };

    const handleSetDefaultPayment = async (methodId: string) => {
        if (!uid) return;
        try {
            // Clear all defaults, then set the chosen one
            await Promise.all(
                paymentMethods.map(m =>
                    updateDoc(doc(db, 'paymentReceivingMethods', m.id), {isDefault: m.id === methodId})
                )
            );
            setPaymentMethods(prev => prev.map(m => ({...m, isDefault: m.id === methodId})));
            toast({position:'top', title:'Default payout method updated', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        }
    };

    const statusSeverity = (s: string) => {
        const l = s?.toLowerCase();
        if (l === 'active') return 'success';
        if (l === 'suspended') return 'danger';
        return 'warning';
    };

    return (
        <>
            <input type="file" ref={fileRef} accept="image/*" style={{display:'none'}}
                   onChange={e => {
                       if (e.target.files?.[0]) {
                           setFile(e.target.files[0]);
                           setImageFile(URL.createObjectURL(e.target.files[0]));
                       }
                   }} />

            <Drawer
                open={openDrawer}
                onClose={() => closeDrawer(false)}
                width={480}
                title={
                    <div style={{display:'flex', alignItems:'center', gap:10}}>
                        <span>Edit Seller</span>
                        {seller?.userStatus && (
                            <Tag severity={statusSeverity(seller.userStatus)} value={seller.userStatus} />
                        )}
                    </div>
                }
                bodyStyle={{padding:'16px 20px', background:'#f7f8fc'}}
                footer={
                    <div style={{display:'flex', justifyContent:'flex-end', gap:10}}>
                        <Button size="sm" flat color="default" onPress={() => closeDrawer(false)}>
                            Cancel
                        </Button>
                        <Button size="sm" color="primary" disabled={saving}
                                onPress={() => form.submit()}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </Button>
                    </div>
                }
            >
                {/* Profile image */}
                <div style={{display:'flex', justifyContent:'center', marginBottom:20}}>
                    <div style={{position:'relative', cursor:'pointer'}}
                         onClick={() => fileRef.current?.click()}>
                        <img
                            src={imageFile ?? seller?.userImage ?? holder}
                            alt="profile"
                            style={{width:100, height:100, borderRadius:'50%', objectFit:'cover',
                                    border:'3px solid #eee'}}
                            onError={e => { (e.target as HTMLImageElement).src = holder; }}
                        />
                        <div style={{
                            position:'absolute', bottom:0, right:0,
                            background:'#29327b', borderRadius:'50%',
                            width:28, height:28, display:'flex', alignItems:'center',
                            justifyContent:'center', color:'#fff', fontSize:13,
                        }}>✎</div>
                    </div>
                </div>

                <Form form={form} layout="vertical" onFinish={handleSave}>

                    {/* ── Profile ── */}
                    <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                                 padding:'14px 16px', marginBottom:14}}>
                        <div style={{fontWeight:600, fontSize:13, color:'#555',
                                     marginBottom:10, letterSpacing:.4}}>PROFILE</div>

                        <Form.Item name="fullName" label="Full Name"
                                   rules={[{required:true, message:'Name is required'}]}>
                            <Input placeholder="Full name" width="100%" onChange={() => {}} />
                        </Form.Item>

                        <Form.Item name="location" label="Location (district)"
                                   rules={[{required:true, message:'Location is required'}]}>
                            <Input placeholder="e.g. Blantyre" width="100%" onChange={() => {}} />
                        </Form.Item>

                        {/* Read-only auth fields */}
                        <InfoRow label="Email"     value={seller?.emailAuth ?? '—'} />
                        <InfoRow label="Phone"     value={seller?.phoneNumberAuth ?? '—'} />
                        <InfoRow label="Joined"    value={seller?.dateJoined
                            ? moment(seller.dateJoined?.toDate ? seller.dateJoined.toDate() : seller.dateJoined).format('DD MMM YYYY')
                            : '—'} />
                    </div>

                    {/* ── Account status ── */}
                    <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                                 padding:'14px 16px', marginBottom:14}}>
                        <div style={{fontWeight:600, fontSize:13, color:'#555',
                                     marginBottom:10, letterSpacing:.4}}>ACCOUNT</div>

                        <Form.Item name="userStatus" label="Status"
                                   rules={[{required:true}]}>
                            <AntSelect>
                                <AntSelect.Option value="active">
                                    <Tag severity="success" value="Active" />
                                </AntSelect.Option>
                                <AntSelect.Option value="disabled">
                                    <Tag severity="warning" value="Disabled" />
                                </AntSelect.Option>
                                <AntSelect.Option value="suspended">
                                    <Tag severity="danger" value="Suspended" />
                                </AntSelect.Option>
                            </AntSelect>
                        </Form.Item>
                    </div>

                    {/* ── Trust / KYC ── */}
                    <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                                 padding:'14px 16px', marginBottom:14}}>
                        <div style={{fontWeight:600, fontSize:13, color:'#555',
                                     marginBottom:10, letterSpacing:.4}}>TRUST &amp; VERIFICATION</div>

                        <Form.Item name="trustTier" label="Trust Tier">
                            <AntSelect>
                                <AntSelect.Option value="none">
                                    <Tag severity="warning" value="Unverified (none)" />
                                </AntSelect.Option>
                                <AntSelect.Option value="basic">
                                    <Tag severity="info" value="Basic (phone verified)" />
                                </AntSelect.Option>
                                <AntSelect.Option value="trusted">
                                    <Tag severity="success" value="Trusted (KYC verified)" />
                                </AntSelect.Option>
                            </AntSelect>
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.trustTier !== cur.trustTier}>
                            {({getFieldValue}) => getFieldValue('trustTier') === 'trusted' && (
                                <Form.Item name="kycExpiresAt" label="KYC Expiry Date"
                                           extra="Leave blank to auto-set to 1 year from today">
                                    <input type="date" style={{
                                        width:'100%', padding:'8px 12px', borderRadius:8,
                                        border:'1px solid #eee', fontSize:13, color:'#333',
                                    }} />
                                </Form.Item>
                            )}
                        </Form.Item>

                        {seller?.kycExpiresAt && (
                            <InfoRow label="Current KYC Expiry" value={
                                (() => {
                                    const d = seller.kycExpiresAt.toDate?.() ?? new Date(seller.kycExpiresAt);
                                    const expired = d < new Date();
                                    return (
                                        <span style={{color: expired ? '#ef4444' : '#22c55e'}}>
                                            {moment(d).format('DD MMM YYYY')} {expired ? '(expired)' : ''}
                                        </span>
                                    );
                                })()
                            } />
                        )}
                    </div>
                </Form>

                {/* ── Payment methods (read-only + set default) ── */}
                <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                             padding:'14px 16px', marginBottom:14}}>
                    <div style={{fontWeight:600, fontSize:13, color:'#555',
                                 marginBottom:10, letterSpacing:.4}}>PAYOUT METHODS</div>

                    {paymentMethods.length === 0 ? (
                        <p style={{fontSize:13, color:'#aaa', margin:0}}>No payout methods configured.</p>
                    ) : (
                        paymentMethods.map(m => (
                            <div key={m.id} style={{
                                display:'flex', justifyContent:'space-between', alignItems:'center',
                                padding:'10px 12px', borderRadius:8, marginBottom:8,
                                border: m.isDefault ? '1.5px solid #29327b' : '1px solid #eee',
                                background: m.isDefault ? '#eef2ff' : '#fafafa',
                            }}>
                                <div>
                                    <div style={{fontWeight:600, fontSize:13}}>
                                        {m.paymentMethod}
                                        {m.isDefault && (
                                            <span style={{fontSize:11, color:'#29327b',
                                                          marginLeft:6, fontWeight:400}}>default</span>
                                        )}
                                    </div>
                                    <div style={{fontSize:12, color:'#888', marginTop:2}}>
                                        {m.paymentMethod === 'Bank Transfer'
                                            ? `${m.bankName ?? ''} · ${m.accountNumber ?? ''} · ${m.accountHolderName ?? ''}`
                                            : m.phoneNumber ?? ''}
                                    </div>
                                </div>
                                {!m.isDefault && (
                                    <button
                                        style={{fontSize:11, color:'#29327b', background:'none',
                                                border:'1px solid #c7d2fe', borderRadius:6,
                                                padding:'3px 10px', cursor:'pointer'}}
                                        onClick={() => handleSetDefaultPayment(m.id)}>
                                        Set default
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>

            </Drawer>
        </>
    );
};

export default EditSeller;
